import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from '../components/PageHeader';
import LoadingFallback from '../components/LoadingFallback';
import EmptyState from '../components/EmptyState';
import InviteDialog from '../components/InviteDialog';
import { api } from '../api/client';
import type { Student, User } from '../api/types';

export default function ParentsPage() {
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);

  const parentsQuery = useQuery<User[]>({
    queryKey: ['users', 'parents'],
    queryFn: async () => (await api.get('/parents')).data,
  });

  return (
    <Box>
      <PageHeader
        title="Parents & guardians"
        subtitle="Recipients of schedule and notification emails."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setInviteOpen(true)}
            aria-label="Invite parent"
          >
            Invite parent
          </Button>
        }
      />

      <Card>
        {parentsQuery.isLoading ? (
          <LoadingFallback />
        ) : (parentsQuery.data ?? []).length === 0 ? (
          <Box sx={{ p: 3 }}>
            <EmptyState
              title="No parents yet"
              description="Invite parents from a student's page to link them to their child."
            />
          </Box>
        ) : (
          <Table size="small" aria-label="Parents">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Children</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(parentsQuery.data ?? []).map((p) => (
                <ParentRow key={p.id} parent={p} />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => qc.invalidateQueries({ queryKey: ['users', 'parents'] })}
        defaultRole="parent"
        roles={['parent']}
      />
    </Box>
  );
}

function ParentRow({ parent }: { parent: User }) {
  const childrenQuery = useQuery<Student[]>({
    queryKey: ['parents', parent.id, 'children'],
    queryFn: async () => (await api.get(`/parents/${parent.id}/children`)).data,
  });

  return (
    <TableRow hover>
      <TableCell>{parent.full_name || '—'}</TableCell>
      <TableCell>{parent.email}</TableCell>
      <TableCell>{parent.phone || '—'}</TableCell>
      <TableCell>
        {childrenQuery.isLoading ? (
          <Typography variant="caption" color="text.secondary">…</Typography>
        ) : (childrenQuery.data ?? []).length === 0 ? (
          <Chip size="small" label="None" variant="outlined" />
        ) : (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(childrenQuery.data ?? []).map((c) => (
              <Chip key={c.id} size="small" label={c.full_name} />
            ))}
          </Stack>
        )}
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          color={parent.is_active ? 'success' : 'default'}
          label={parent.is_active ? 'Active' : 'Inactive'}
          variant={parent.is_active ? 'filled' : 'outlined'}
        />
      </TableCell>
    </TableRow>
  );
}
